import { useContext, useMemo } from 'react';
import SortOption from '~/constants/SortOption';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import { useHasAppliedTrade } from '~/contexts/SearchContext';
import { useVerboseLogging } from '~/contexts/StaticContext';
import { useUserLocation } from '~/contexts/UserContext';
import type { RequestedFeature } from '~/contracts';
import useCheckExperimentBucket from '~/hooks/useCheckExperimentBucket';
import useGenerateAnalyticsData from '~/hooks/useGenerateAnalyticsData';
import type {
  InventoryRequest,
  InventoryRequestAnalytics
} from '~/services/contracts';
import { isDefined } from '~/utilities/array';
import BrowserCookies from '~/utilities/BrowserCookies';

const baseRequestedFeatures: RequestedFeature[] = [
  'EarliestAcquisitionBoosting',
  'ExcludeFacetData',
  'LoanTermPricing',
  'LocationBasedPrefiltering',
  'Personalization'
];

const baseLimitedRequestFeatures: RequestedFeature[] = [
  'ExcludeFacetData',
  'LocationBasedPrefiltering'
];

const dummyAnalyticsData: InventoryRequestAnalytics = {
  browser: '',
  clientId: '',
  deviceName: '',
  isFirstActiveSearchSession: false,
  isMobileDevice: false,
  previousSearchRequestId: '',
  referrer: '',
  searchRequestId: ''
};

type Options = {
  includeAnalyticsData: boolean;
  includePageNumber: boolean;
  limitRequestedFeatures: boolean;
};

const defaultOptions: Options = {
  includeAnalyticsData: true,
  includePageNumber: true,
  limitRequestedFeatures: false
};

export default function useGenerateInventoryRequest(
  pageSize: number,
  options: Partial<Options> = {}
): {
  request: InventoryRequest;
  conditionalRequestedFeatures: RequestedFeature[];
} {
  const opts: Options = { ...defaultOptions, ...options };

  const { appliedFilters, pageNumber, sortBy } = useContext(
    AppliedFiltersContext
  );
  const analyticsData = useGenerateAnalyticsData();
  const { zip = '' } = useUserLocation();
  const shouldApplySprinkles = sortBy === SortOption.MostPopular;
  const verboseLoggingEnabled = useVerboseLogging();
  const hideImpossibleCombinations = useCheckExperimentBucket(
    'srch-impossible-facets',
    'experiment'
  );

  const requestedFeatures = useMemo<RequestedFeature[]>(
    () =>
      (opts.limitRequestedFeatures
        ? baseLimitedRequestFeatures
        : ([] as Array<RequestedFeature | undefined>)
            .concat(baseRequestedFeatures)
            .concat(verboseLoggingEnabled ? 'ApplyVerboseLogging' : undefined)
            .concat(shouldApplySprinkles ? 'Sprinkles' : undefined)
      )
        .concat(hideImpossibleCombinations ? 'HideImpossibleCombos' : undefined)
        .filter(isDefined),
    [
      hideImpossibleCombinations,
      opts.limitRequestedFeatures,
      shouldApplySprinkles,
      verboseLoggingEnabled
    ]
  );

  const hasAppliedTrade = useHasAppliedTrade();
  const conditionalRequestedFeatures = useMemo<RequestedFeature[]>(
    () => [hasAppliedTrade ? 'ApplyTradeIn' : 'FetchTradeIn'],
    [hasAppliedTrade]
  );

  const analyticsDataToSend = opts.includeAnalyticsData
    ? analyticsData
    : dummyAnalyticsData;
  const page = opts.includePageNumber ? pageNumber : 1;

  const request = useMemo<InventoryRequest>(
    () => ({
      analyticsData: analyticsDataToSend,
      browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
      filters: appliedFilters,
      pagination: { page, pageSize },
      requestedFeatures,
      sortBy,
      zip5: zip
    }),
    [
      analyticsDataToSend,
      appliedFilters,
      page,
      pageSize,
      requestedFeatures,
      sortBy,
      zip
    ]
  );

  return {
    request,
    conditionalRequestedFeatures
  };
}
